/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.storage

import io.peekandpoke.klang.common.logging.LogArea
import io.peekandpoke.klang.common.logging.Logger
import io.peekandpoke.ultra.streams.Stream
import io.peekandpoke.ultra.streams.StreamSource
import kotlinx.browser.localStorage

private val log = Logger(area = LogArea.Storage, source = "EditorSettings")

/**
 * The code editor's settings, kept in the browser's localStorage (KLANG-00349; the song page's Settings tool window):
 * how many highlights a sound event draws, and whether a name's quick help shows on hover: on by default (the
 * maintainer, 2026-10-27; off from 2026-10-26, when Alt+Q came to show it in the Help window), and a user's own
 * choice kept.
 *
 * The storage can throw or come back empty (a private window, blocked site data); the settings then start at their
 * defaults and only forget on reload.
 */
object EditorSettings {

    /** Highlights per sound event, as the song page always had. */
    const val HIGHLIGHTS_PER_EVENT_DEFAULT = 25

    /** The highlights' range a user may choose; 0 draws none, as the header's EVT field allowed. */
    val HIGHLIGHTS_PER_EVENT_RANGE = 0..50

    private val highlightsSource = StreamSource(
        read(StorageKeys.EDITOR_HIGHLIGHTS_PER_EVENT)?.toIntOrNull()
            ?.coerceIn(HIGHLIGHTS_PER_EVENT_RANGE) ?: HIGHLIGHTS_PER_EVENT_DEFAULT
    )

    /** The hover's quick help unless the user turned it off ("false" kept); on for anyone who never chose. */
    private val hoverHelpSource = StreamSource(read(StorageKeys.EDITOR_HOVER_HELP) != "false")

    val highlightsPerEvent: Stream<Int> = highlightsSource

    val hoverHelp: Stream<Boolean> = hoverHelpSource

    fun setHighlightsPerEvent(value: Int) {
        val next = value.coerceIn(HIGHLIGHTS_PER_EVENT_RANGE)

        highlightsSource(next)
        write(StorageKeys.EDITOR_HIGHLIGHTS_PER_EVENT, next.toString())
    }

    fun setHoverHelp(on: Boolean) {
        hoverHelpSource(on)
        write(StorageKeys.EDITOR_HOVER_HELP, on.toString())
    }

    private fun read(key: String): String? = try {
        localStorage.getItem(key)
    } catch (e: Throwable) {
        log.warn("Could not read $key", e)
        null
    }

    private fun write(key: String, value: String) {
        try {
            localStorage.setItem(key, value)
        } catch (e: Throwable) {
            log.warn("Could not remember $key", e)
        }
    }
}
